import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  Alert,
  Spinner,
  Card,
  CardBody,
} from 'reactstrap';
import {
  validateDiscountCode,
  resetMessage,
} from '../../../store/discountCodes/actions';
import {
  selectDiscountCodeValidationData,
  selectDiscountCodeLoading,
  selectDiscountCodeError,
} from '../../../selectors/discountCode';

const DiscountApplication = ({
  plan,
  appliedDiscount,
  onDiscountApplied,
  onDiscountRemoved,
  onNext,
  onPrevious,
}) => {
  const dispatch = useDispatch();
  const [code, setCode] = useState('');
  const [success, setSuccess] = useState('');
  const [localAppliedDiscount, setLocalAppliedDiscount] =
    useState(appliedDiscount);

  // Redux selectors
  const validationData = useSelector(selectDiscountCodeValidationData);
  const loading = useSelector(selectDiscountCodeLoading);
  const error = useSelector(selectDiscountCodeError);

  // Helper function to parse price (same as in CreateSubscription)
  const parsePrice = (priceString) => {
    if (typeof priceString === 'number') return priceString;
    if (typeof priceString === 'string') {
      return parseFloat(priceString.replace(',', '.'));
    }
    return 0;
  };

  // Calculate prices
  const originalPriceString = plan.promoPrice || plan.price;
  const originalPrice = parsePrice(originalPriceString);
  const finalPrice = localAppliedDiscount
    ? localAppliedDiscount.newPrice
    : originalPrice;
  const savings = localAppliedDiscount ? originalPrice - finalPrice : 0;

  useEffect(() => {
    if (validationData) {
      setLocalAppliedDiscount(validationData);

      if (validationData.isFree) {
        setSuccess(`Codice applicato! Il piano è ora gratuito.`);
      } else {
        setSuccess(
          `Codice applicato! Nuovo prezzo: € ${validationData.newPrice.toFixed(
            2,
          )} (era € ${validationData.originalPrice.toFixed(2)})`,
        );
      }

      if (validationData.disableAutoRenewal) {
        setSuccess(
          (prev) => prev + ' Il rinnovo automatico è stato disabilitato.',
        );
      }

      onDiscountApplied(validationData);
    }
  }, [validationData, onDiscountApplied]);

  useEffect(() => {
    if (error && localAppliedDiscount) {
      setLocalAppliedDiscount(null);
      setSuccess('');
      onDiscountRemoved();
    }
  }, [error, localAppliedDiscount, onDiscountRemoved]);

  // Initialize with existing discount if any
  useEffect(() => {
    if (appliedDiscount && !localAppliedDiscount) {
      setLocalAppliedDiscount(appliedDiscount);
      setCode(appliedDiscount.code || '');
    }
  }, [appliedDiscount, localAppliedDiscount]);

  const handleValidateDiscountCode = () => {
    if (!code.trim()) {
      alert('Inserisci un codice sconto');
      return;
    }

    setSuccess('');
    dispatch(validateDiscountCode(code.trim(), plan));
  };

  const removeDiscount = () => {
    setCode('');
    setSuccess('');
    setLocalAppliedDiscount(null);
    dispatch(resetMessage());
    onDiscountRemoved();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleValidateDiscountCode();
    }
  };

  const skipDiscount = () => {
    onNext();
  };

  return (
    <div className="step-content">
      <div className="row justify-content-center">
        <div className="col-12">
          <div className="text-center mb-4">
            <h3 className="mb-2">Applica un codice sconto</h3>
            <p className="text-muted">
              Hai un codice sconto? Inseriscilo per ottenere il prezzo scontato
            </p>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-12 col-md-8">
          {/* Current Plan Summary */}
          <Card className="mb-4 border-light">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1">{plan.title}</h5>
                  <p className="text-muted mb-0">{plan.subtitle}</p>
                </div>
                <div className="text-right">
                  {localAppliedDiscount?.isFree ? (
                    <h4 className="text-success mb-0">Gratuito</h4>
                  ) : (
                    <>
                      {localAppliedDiscount && (
                        <div className="text-muted text-decoration-line-through small">
                          €{originalPrice.toFixed(2)}
                        </div>
                      )}
                      <h4
                        className={`mb-0 ${
                          localAppliedDiscount ? 'text-success' : 'text-primary'
                        }`}>
                        €{finalPrice.toFixed(2)}
                        <small className="text-muted">
                          /{plan.periodLabel}
                        </small>
                      </h4>
                    </>
                  )}
                  {savings > 0 && !localAppliedDiscount?.isFree && (
                    <small className="text-success">
                      Risparmi €{savings.toFixed(2)}
                    </small>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Discount Code Section */}
          <Card>
            <div className="card-header">
              <h5 className="mb-0">
                <i className="bx bxs-coupon mr-2"></i>
                Codice Sconto
              </h5>
            </div>
            <CardBody>
              {!localAppliedDiscount ? (
                <>
                  <p className="text-muted mb-3">
                    Inserisci il codice sconto per applicare il prezzo scontato
                    al tuo piano.
                  </p>
                  <InputGroup className="mb-3">
                    <Input
                      type="text"
                      placeholder="Inserisci il codice sconto"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      onKeyPress={handleKeyPress}
                      disabled={loading}
                    />
                    <InputGroupAddon addonType="append">
                      <Button
                        color="primary"
                        onClick={handleValidateDiscountCode}
                        disabled={loading || !code.trim()}>
                        {loading ? <Spinner size="sm" /> : 'Applica'}
                      </Button>
                    </InputGroupAddon>
                  </InputGroup>
                </>
              ) : (
                <div className="applied-discount">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 text-success">
                        <i className="bx bx-check-circle mr-1"></i>
                        Codice "{localAppliedDiscount.code}" applicato
                      </h6>
                      <p className="mb-0 text-muted">
                        {localAppliedDiscount.description ||
                          'Sconto applicato con successo'}
                      </p>
                    </div>
                    <Button
                      color="outline-secondary"
                      size="sm"
                      onClick={removeDiscount}>
                      Rimuovi
                    </Button>
                  </div>
                </div>
              )}

              {error && (
                <Alert color="danger" className="mt-3 mb-0">
                  <i className="bx bx-error-circle mr-2"></i>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert color="success" className="mt-3 mb-0">
                  <i className="bx bx-check-circle mr-2"></i>
                  {success}
                </Alert>
              )}
            </CardBody>
          </Card>

          {/* Optional Skip Section */}
          {!localAppliedDiscount && (
            <div className="text-center mt-4">
              <button
                type="button"
                className="btn btn-link text-muted"
                onClick={skipDiscount}>
                Non ho un codice sconto, continua
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="row justify-content-center mt-4">
        <div className="col-12 col-md-8">
          <div className="twitter-bs-wizard-pager-link d-flex justify-content-between flex-column flex-sm-row mb-5">
            <button
              type="button"
              className="btn btn-outline-secondary btn-rounded waves-effect waves-light px-4 mb-2 mb-sm-0"
              onClick={onPrevious}>
              <i className="bx bx-chevron-left mr-2"></i>
              Indietro
            </button>
            <button
              type="button"
              className="btn btn-primary btn-rounded waves-effect waves-light px-4"
              onClick={onNext}>
              Continua
              <i className="bx bx-chevron-right ml-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountApplication;
